import { NgClass } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import type { BackofficeInformationRequestCard } from 'types/information-request.interface';
import type { StructuralUnitMinimalListItem } from 'types/structural-unit.interface';
import { TooltipComponent } from 'components/tooltip/tooltip.component';
import { emailValidator } from 'util/form.utility';

const MAX_NOTE_LENGTH = 255;

export type MarkSentDeliveryMethod = 'Email' | 'EAddress' | 'PostalMail' | 'Archive';

export interface InvoiceMarkSentPayload {
  actualDeliveryMethod: MarkSentDeliveryMethod;
  addressDetail: string | null;
  backofficeNote: string | null;
}

@Component({
  selector: 'app-ir-invoice-mark-sent-modal',
  standalone: true,
  imports: [NgClass, ReactiveFormsModule, TranslateModule, TooltipComponent],
  templateUrl: './mark-sent-modal.component.html'
})
export class IrInvoiceMarkSentModalComponent implements OnChanges {
  readonly MAX_NOTE_LENGTH = MAX_NOTE_LENGTH;
  @Input() isOpen = false;
  @Input() informationRequest!: BackofficeInformationRequestCard;
  @Input() structuralUnits: StructuralUnitMinimalListItem[] = [];
  @Input() isLoading = false;
  @Output() confirmed = new EventEmitter<InvoiceMarkSentPayload>();
  @Output() closed = new EventEmitter<void>();
  @ViewChild('content') content!: TemplateRef<unknown>;

  readonly deliveryOptions: { value: MarkSentDeliveryMethod; label: string }[] = [
    { value: 'Email', label: 'pages.viewInfoRequest.invoice.deliveryMethod.Email' },
    { value: 'EAddress', label: 'pages.viewInfoRequest.invoice.deliveryMethod.EAddress' },
    { value: 'PostalMail', label: 'pages.viewInfoRequest.invoice.deliveryMethod.PostalMail' },
    { value: 'Archive', label: 'pages.viewInfoRequest.invoice.deliveryMethod.Archive' }
  ];

  form: FormGroup;

  constructor(
    private fb: FormBuilder,
    private modalService: NgbModal
  ) {
    this.form = this.fb.group({
      actualDeliveryMethod: ['', Validators.required],
      addressDetail: [''],
      backofficeNote: ['', Validators.maxLength(MAX_NOTE_LENGTH)]
    });
    this.form.get('actualDeliveryMethod')?.valueChanges.subscribe((method: MarkSentDeliveryMethod | '') => {
      const addressControl = this.form.get('addressDetail') as AbstractControl;
      addressControl.clearValidators();
      if (method === 'Email') {
        addressControl.setValidators([Validators.required, emailValidator()]);
      } else if (method === 'Archive') {
        addressControl.setValidators([Validators.required]);
      }
      addressControl.setValue(this.resolveAddressDefault(method));
      addressControl.updateValueAndValidity();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['isOpen']) return;
    if (this.isOpen) {
      this.form.reset({ actualDeliveryMethod: '', addressDetail: '', backofficeNote: '' });
      this.modalService.open(this.content, { backdrop: 'static', keyboard: false, windowClass: 'send-email-modal' });
    } else {
      this.modalService.dismissAll();
    }
  }

  private resolveAddressDefault(method: MarkSentDeliveryMethod | ''): string {
    const req = this.informationRequest;
    if (!req || !method) return '';
    if (method === 'Email') {
      if (req.invoiceDeliveryMethod === 'Email' && req.invoiceDeliveryDetail) {
        return req.invoiceDeliveryDetail;
      }
      return req.contactPersonEmail || req.legalEntity?.email || req.clientInfo?.email || req.client?.email || '';
    }
    if (method === 'PostalMail') {
      if (req.invoiceDeliveryMethod === 'PostalMail' && req.invoiceDeliveryDetail) {
        return req.invoiceDeliveryDetail;
      }
      const responseAddress = req.responseDeliveryMethod === 'PostalMail' ? req.responseDeliveryDetail : null;
      return req.clientInfo?.legalAddress || req.legalEntity?.legalAddress || responseAddress || '';
    }
    if (method === 'Archive') {
      return req.invoiceDeliveryStructuralUnitName || req.responseDeliveryStructuralUnitName || req.responsibleStructuralUnitName || '';
    }
    return '';
  }

  confirm(): void {
    if (this.form.invalid || this.isLoading) return;
    const val = this.form.getRawValue();
    this.confirmed.emit({
      actualDeliveryMethod: val.actualDeliveryMethod,
      addressDetail: val.addressDetail || null,
      backofficeNote: val.backofficeNote || null
    });
  }

  close(): void {
    this.modalService.dismissAll();
    this.closed.emit();
  }
}
